import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_LOCALE, LOCALE_CODES, LOCALE_STORAGE_KEY } from './locales';
import { getContent } from './registry';

const LocaleContext = createContext({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  content: getContent(DEFAULT_LOCALE),
  isTranslated: false,
});

function storedLocale() {
  try {
    const value = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return LOCALE_CODES.includes(value) ? value : null;
  } catch (error) {
    return null; // private browsing
  }
}

/**
 * First visit only: honour the browser's language preferences, in the order the
 * visitor set them. Any Chinese variant maps to Simplified — the only Chinese
 * draft we hold — and anything we do not publish falls through to English.
 */
function detectedLocale() {
  const tags = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of tags) {
    const base = String(tag || '').toLowerCase();
    if (base.startsWith('zh')) return 'zh';
    if (base.startsWith('ms') || base.startsWith('id')) return 'ms';
    if (base.startsWith('en')) return 'en';
  }
  return null;
}

/**
 * `?lang=` seeds the locale on first load, so a translated page stays shareable.
 * Then a remembered choice, then the browser's own languages on a first visit.
 * After that the choice is component state: React Router applies navigations in
 * a transition, so reading the active locale back off the URL would leave the
 * page a render behind the switch the visitor just made.
 */
export function LocaleProvider({ children }) {
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState(() => {
    const fromUrl = params.get('lang');
    if (LOCALE_CODES.includes(fromUrl)) return { locale: fromUrl, source: 'url' };
    const remembered = storedLocale();
    if (remembered) return { locale: remembered, source: 'choice' };
    const detected = detectedLocale();
    if (detected && detected !== DEFAULT_LOCALE) return { locale: detected, source: 'browser' };
    return { locale: DEFAULT_LOCALE, source: 'default' };
  });
  const { locale, source } = state;

  const setLocale = useCallback(
    (next) => {
      if (!LOCALE_CODES.includes(next)) return;
      setState({ locale: next, source: 'choice' });
      try {
        window.localStorage.setItem(LOCALE_STORAGE_KEY, next);
      } catch (error) {
        // private browsing — the choice simply does not persist
      }
      const draft = new URLSearchParams(params);
      if (next === DEFAULT_LOCALE) draft.delete('lang');
      else draft.set('lang', next);
      setParams(draft, { replace: true });
    },
    [params, setParams]
  );

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      content: getContent(locale),
      isTranslated: locale !== DEFAULT_LOCALE,
      autoDetected: source === 'browser',
    }),
    [locale, source, setLocale]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  return useContext(LocaleContext);
}

export function useContent() {
  return useContext(LocaleContext).content;
}
